<template>
  <Card
    class="flex h-full min-w-0 w-full flex-col overflow-hidden rounded-[1.25rem] border border-[#d8e1f0] bg-white text-[#1f2937] shadow-[0_18px_40px_rgba(15,23,42,0.06)]"
  >
    <CardHeader
      class="border-b border-[#e5ecf5] bg-[#f8fbff]"
      :class="comfortMode === 'comfortable' ? 'px-6 md:px-8 py-5 md:py-6' : 'px-6 py-5'"
    >
      <div
        class="flex items-start justify-between gap-4 max-[640px]:flex-col max-[640px]:items-stretch"
      >
        <div class="space-y-1">
          <CardTitle
            :class="
              cn(
                'm-0 font-black text-[#0f172a]',
                comfortMode === 'comfortable' ? 'text-lg md:text-xl' : 'text-[1.05rem] font-extrabold'
              )
            "
          >
            {{ role === 'client' ? 'Monthly Progress' : 'Progress Flow' }}
          </CardTitle>
          <CardDescription
            :class="
              cn(
                'font-medium mt-1 text-[#64748b]',
                comfortMode === 'comfortable' ? 'text-sm md:text-base' : 'text-[0.88rem]'
              )
            "
          >
            {{ role === 'client' ? 'Shows how your completed and active work changed during the reporting period.' : chartDescription }}
          </CardDescription>
        </div>
      </div>
    </CardHeader>

    <CardContent class="flex-1 p-0">
      <div
        v-if="loading"
        class="space-y-4 p-6"
      >
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Skeleton
            v-for="n in 4"
            :key="n"
            class="h-[4.75rem] rounded-[1rem] bg-[#eef4ff]"
          />
        </div>
        <Skeleton class="h-[20rem] rounded-[1.15rem] bg-[#eef4ff]" />
      </div>

      <div
        v-else-if="!hasData"
        class="px-6 py-6"
      >
        <div
          class="grid min-h-[24rem] place-items-center rounded-[1.15rem] border border-dashed border-[#dbe5f2] bg-[#fafcff] px-6 text-center"
        >
          <div class="space-y-2">
            <p class="text-[1rem] font-bold text-[#0f172a]">
              No progress data yet
            </p>
            <p class="text-[0.92rem] leading-7 text-[#64748b] font-medium">
              Progress metrics will appear once tasks start moving through the pipeline.
            </p>
          </div>
        </div>
      </div>

      <div
        v-else
        class="p-6 text-left"
        :class="comfortMode === 'comfortable' ? 'space-y-6 md:p-8' : 'space-y-5 p-6'"
      >
        <div
          class="rounded-[1.15rem] border border-[#dbe5f2] bg-white p-5"
        >
          <div class="mb-4 flex items-center justify-between gap-3">
            <div>
              <p
                :class="
                  cn(
                    'font-bold uppercase tracking-[0.14em] text-[#64748b]',
                    comfortMode === 'comfortable' ? 'text-xs md:text-sm' : 'text-[0.72rem]'
                  )
                "
              >
                Tasks
              </p>
            </div>
          </div>
          <div
            :class="comfortMode === 'comfortable' ? 'h-[25rem]' : 'h-[20rem]'"
          >
            <canvas ref="canvasRef" />
          </div>
        </div>

        <div
          v-if="role === 'client'"
          class="rounded-2xl border border-blue-100 bg-[#eff6ff]/35"
          :class="comfortMode === 'comfortable' ? 'p-5 md:p-6' : 'p-3.5'"
        >
          <p
            :class="
              cn(
                'font-extrabold uppercase tracking-wider text-[#1e40af] mb-1.5',
                comfortMode === 'comfortable' ? 'text-xs md:text-sm' : 'text-[0.72rem]'
              )
            "
          >
            Insight
          </p>
          <p
            :class="
              cn(
                'font-semibold text-slate-700 leading-relaxed',
                comfortMode === 'comfortable' ? 'text-sm md:text-base' : 'text-[0.82rem]'
              )
            "
          >
            Completed work increases and active tasks adjust as deliverables move toward final publication.
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, nextTick, ref, watch, inject } from "vue";
import Chart from "chart.js/auto";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type {
  DashboardProgressFlowItem,
  DashboardSummary,
} from "@/types/dashboard";

const props = defineProps<{
  data: DashboardProgressFlowItem[];
  loading?: boolean;
  role?: string | null;
  summary?: DashboardSummary | null;
}>();

const comfortMode = inject<any>("comfortMode", ref("comfortable"));

const canvasRef = ref<HTMLCanvasElement | null>(null);
let chart: Chart | null = null;

const chartDescription = computed(() =>
  String(props.role || "").toLowerCase() === "client"
    ? "Visualize work momentum and completion trends across your active account."
    : "Visualize work momentum and completion trends across your active account."
);

const chartData = computed(() => [
  {
    label: "Not Started",
    value: props.data.map((item) => Number(item.not_started) || 0),
    color: "#94a3b8",
    fill: true,
  },
  {
    label: "In Progress",
    value: props.data.map((item) => Number(item.in_progress) || 0),
    color: "#3b82f6",
    fill: true,
  },
  {
    label: "Completed",
    value: props.data.map(
      (item) => (Number(item.done) || 0) + (Number(item.closed) || 0)
    ),
    color: "#10b981",
    fill: true,
  },
]);

const hasData = computed(() =>
  props.data.some(
    (item) =>
      (Number(item.not_started) || 0) > 0 ||
      (Number(item.in_progress) || 0) > 0 ||
      (Number(item.done) || 0) > 0 ||
      (Number(item.closed) || 0) > 0
  )
);

function destroyChart() {
  chart?.destroy();
  chart = null;
}

function renderChart() {
  if (!canvasRef.value || !hasData.value) return;

  destroyChart();

  const labels = props.data.map((item) => item.date);

  const isComfortable = comfortMode.value === "comfortable";
  const tickFontSize = isComfortable ? 13 : 11;
  const legendFontSize = isComfortable ? 13 : 11;

  chart = new Chart(canvasRef.value, {
    type: "line",
    data: {
      labels,
      datasets: chartData.value.map((series) => ({
        label: series.label,
        data: series.value,
        borderColor: series.color,
        backgroundColor: series.fill ? `${series.color}33` : "transparent",
        fill: series.fill,
        tension: 0.35,
        pointRadius: 0,
        borderWidth: 2,
        stack: "progress",
      })),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            color: "rgba(71,85,105,0.95)",
            usePointStyle: true,
            boxWidth: 10,
            boxHeight: 10,
            padding: 16,
            font: { size: legendFontSize, weight: "600" },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            color: "rgba(226,232,240,0.95)",
            borderDash: [4, 4],
          },
          ticks: {
            color: "rgba(71,85,105,0.9)",
            maxRotation: 0,
            autoSkip: true,
            font: { size: tickFontSize },
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          suggestedMax: Math.max(
            ...props.data.map(
              (item) =>
                (Number(item.not_started) || 0) +
                (Number(item.in_progress) || 0) +
                (Number(item.done) || 0) +
                (Number(item.closed) || 0)
            ),
            5
          ),
          grid: {
            color: "rgba(226,232,240,0.95)",
            borderDash: [4, 4],
          },
          ticks: {
            color: "rgba(71,85,105,0.9)",
            font: { size: tickFontSize },
            precision: 0,
          },
        },
      },
    },
  });
}

watch(
  () => props.data,
  () => nextTick(renderChart),
  { deep: true, immediate: true }
);

onBeforeUnmount(() => {
  destroyChart();
});
</script>
